import { useTranslations } from "@fenetech/translations";
import { AlertColor } from "@mui/material";
import ESignatureAPI from "components/Setup/ESignature/ESignatureAPI";
import useApplicationInfo from "helpers/context/Application/useApplicationInfo";
import useWait from "helpers/context/Page/useWait";
import useUserInfo from "helpers/context/User/useUserInfo";
import { DocuSignEnvelopeStatusEnum, ESignatureRedirectSourceEnum, RoleEnum } from "helpers/enums";
import { useEffectOnLoad } from "helpers/hooks/useEffectOnLoad";
import { GetParamCaseInsensitive, isNullOrWhiteSpace } from "helpers/objects";
import { IESignatureDocument, IEsignatureSettings } from "models/IESignature";
import { IQuote } from "models/IQuote";
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from "react-router-dom";

interface IProps {
    quote: IQuote | undefined,
    eSignatureSettings: IEsignatureSettings | null,
    setESignatureSettings: React.Dispatch<React.SetStateAction<IEsignatureSettings | null>>,
    setESignatureDocument: React.Dispatch<React.SetStateAction<IESignatureDocument | null>>,
    setAlert: React.Dispatch<React.SetStateAction<[text: string, alertType: AlertColor] | null>>
}

const useESignatureQuoteHelper = ({ quote, eSignatureSettings, setESignatureSettings, setESignatureDocument, setAlert }: IProps) => {

    const wait = useWait();
    const tm = useTranslations();
    const appInfo = useApplicationInfo();
    const [query, setQuery] = useSearchParams();
    const user = useUserInfo();

    const eSignMessage: string | null = useMemo(() => {
        return GetParamCaseInsensitive(query, "eSignMessage") ?? null;
    }, [query]);

    const embeddedEvent: string | null = useMemo(() => {
        return GetParamCaseInsensitive(query, "event") ?? null;
    }, [query]);

    useEffectOnLoad(() => {
        //Check if we have a message when loading in. If so, we just came from the 3rd party login
        if (!isNullOrWhiteSpace(eSignMessage)) {
            setAlert([eSignMessage!, "error"]);
        }
    });

    const refreshESignatureDocument = useCallback(async () => {
        if (quote) {
            const eSignDocument: IESignatureDocument = await ESignatureAPI.GetQuoteDocument(quote.mfgCustomerID, quote.mfgSiteID, quote.oKey);
            setESignatureDocument(eSignDocument);
        }
    }, [quote, setESignatureDocument]);

    //https://developers.docusign.com/docs/esign-rest-api/reference/envelopes/envelopeviews/createrecipient/
    const getESignatureStatusFromEmbeddedEvent = (embeddedEvent: string) => {
        switch (embeddedEvent) {
            case "signing_complete":
                return DocuSignEnvelopeStatusEnum.Completed;
            case "decline":
                return DocuSignEnvelopeStatusEnum.Declined;
        }
        return DocuSignEnvelopeStatusEnum.NotStarted;
    };

    const getESignatureInfo = useCallback(async () => {
        if (quote) {
            try {
                wait.Show(true);
                const settingsRes: IEsignatureSettings = await ESignatureAPI.GetESignSettings(quote.mfgCustomerID, quote.mfgSiteID);
                setESignatureSettings(settingsRes);
    
                if (settingsRes.isEnabled) {
                    if (embeddedEvent) {
                        const embeddedRes: boolean = await ESignatureAPI.UpdateESignatureDocumentStatus(quote.oKey, getESignatureStatusFromEmbeddedEvent(embeddedEvent));
                        if (embeddedRes) {
                            //Clear the query param out. We don't want to keep hitting this logic if the user refreshes the page
                            const updatedQuery = new URLSearchParams(query.toString());
                            updatedQuery.delete("event");
                            setQuery(updatedQuery, { replace: true})
                        }
                    }
                    await refreshESignatureDocument();
                }
            } finally {
                wait.Show(false);
            }
        }
    }, [quote, wait, refreshESignatureDocument, setESignatureSettings, embeddedEvent, query, setQuery]);

    const callbackURI = useMemo(() => {
        return document.getElementsByTagName("base")[0].href + "setup/esign/callback";
    }, []);

    const launchESignatureConsentLogin = useCallback(async () => {
        if (quote) {
            const authResponse = await ESignatureAPI.GetAuthURIFromOKey(quote.oKey, callbackURI, ESignatureRedirectSourceEnum.Quote);
            if (!isNullOrWhiteSpace(authResponse.message)) {
                setAlert([tm.Get(authResponse.message), "error"]);
            } else {
                window.location.assign(authResponse.payload);
            }
        }
    }, [quote, callbackURI, tm, setAlert]);

    useEffect(() => {
        if (appInfo.parameters.eSignatureEnabled && user.HasRole(RoleEnum.Dealer) && !eSignatureSettings) {
            getESignatureInfo();
        }
    }, [appInfo, user, eSignatureSettings, getESignatureInfo]);

    return {
        refreshESignatureDocument,
        launchESignatureConsentLogin
    }
}

export default useESignatureQuoteHelper;